import React, { useEffect, useState } from 'react';
import { Menu } from 'antd';
import { useLocation } from 'react-router';

import {
    useAccountStore,
    useManagementStore,
    useSettingStore,
    useUserState
} from '../../../../store';
import { getCompanyFromAccountUniqueId } from '../../../../utilities/account-ids';

import './styles.scss';

const SubHeader = () => {
    const { allAccounts, changeAccount } = useAccountStore((state) => state);
    const { getUser } = useUserState();
    const { pathname } = useLocation();
    const { filter, changeFilter } = useSettingStore((state) => state);
    const [userAccounts, setUserAccounts] = useState([]);
    const unassignedRouteMatch = pathname.match(/^\/admin\/u\/([^/]+)/i);
    const normalizedUnassignedId = unassignedRouteMatch
        ? unassignedRouteMatch[1].toUpperCase()
        : '';
    const routeScopedCompany = getCompanyFromAccountUniqueId(normalizedUnassignedId);
    const onChangeAccounts = ({ key }) => {
        if (!key) {
            changeFilter('accounts', []);
            return;
        }

        changeFilter('accounts', [key]);
        const selectedAccount = allAccounts.find((account) => account.key === key);
        if (selectedAccount) changeAccount(selectedAccount);
    };
    const { viewAs, company, publicView } = useManagementStore((state) => state);
    const isViewAsActive =
        JSON.stringify(viewAs?.newUser?.token || null) !==
        JSON.stringify(viewAs?.mainUser?.token || null);
    const user = getUser();
    const effectiveCompany = routeScopedCompany || company;
    const normalizedPublicId =
        typeof publicView?.id === 'string' ? publicView.id.toUpperCase() : '';
    const hasSelectedAccounts = Array.isArray(filter.accounts) && filter.accounts.length > 0;
    function matchesPublicAccountType(account) {
        if (!publicView.isActive) return true;
        if (normalizedPublicId.startsWith('TRPTT')) return account.ptt_account === true;
        if (normalizedPublicId.startsWith('TRPCT')) return account.pct_account === true;
        if (
            normalizedPublicId.startsWith('TTD') ||
            normalizedPublicId.startsWith('TT') ||
            normalizedPublicId.startsWith('TTF')
        ) {
            return account.simulator_account === true || account.broker === 'simulator';
        }

        return account.liveAccount === true || account.simulator_account === true;
    }

    const pickPublicTargetAccount = (accounts = []) => {
        if (!Array.isArray(accounts) || accounts.length === 0) return null;

        const normalize = (value) => (typeof value === 'string' ? value.trim().toUpperCase() : '');
        const id = normalize(normalizedPublicId);

        if (!id) return accounts[0] || null;

        const exactMatch = accounts.find(
            (acc) => normalize(acc?.label) === id || normalize(acc?.name) === id
        );
        if (exactMatch) return exactMatch;

        let candidates = [];
        if (id.startsWith('TRPTT')) {
            candidates = accounts.filter(
                (acc) => acc?.ptt_account === true && acc?.liveAccount === true
            );
        } else if (id.startsWith('TRPCT')) {
            candidates = accounts.filter(
                (acc) => acc?.pct_account === true && acc?.liveAccount === true
            );
        } else if (id.startsWith('TTD') || id.startsWith('TT') || id.startsWith('TTF')) {
            candidates = accounts.filter(
                (acc) => acc?.simulator_account === true || acc?.broker === 'simulator'
            );
        }

        if (candidates.length === 0) {
            candidates = accounts.filter((acc) => matchesPublicAccountType(acc));
        }

        if (candidates.length === 0) return null;

        candidates.sort((a, b) => (b?.numberOfTrades || 0) - (a?.numberOfTrades || 0));
        return candidates[0] || null;
    };

    // Checking all accounts in case it's first access and store doesn't contain any filter changes
    useEffect(() => {
        if (allAccounts.length === 0) {
            if (hasSelectedAccounts) changeFilter('accounts', []); // Make sure filter is empty if no accounts
            setUserAccounts([]);
            return;
        }

        // else if (Object.keys(filter.accounts).length === 0 && allAccounts.length > 0) {

        //   const liveTradeAccount = allAccounts?.filter((account) => account.liveAccount === true);
        //   if (isViewAsActive === false) {

        //     onChangeAccounts([allAccounts[0].key]);

        //   } else if (liveTradeAccount.length > 0) {

        //     onChangeAccounts([liveTradeAccount[0].key]);

        //   }

        //   onChangeAccounts([]);

        // }

        if (allAccounts.length > 0) {
            if (publicView.isActive) {
                const targetAccount = pickPublicTargetAccount(allAccounts);

                if (!targetAccount) {
                    if (hasSelectedAccounts) changeFilter('accounts', []);
                    setUserAccounts([]);
                    return;
                }

                const publicAccount = { ...targetAccount, label: 'Public View' };
                setUserAccounts([publicAccount]);

                const selectedKey = Array.isArray(filter.accounts) ? filter.accounts[0] : null;
                if (selectedKey !== publicAccount.key) {
                    changeFilter('accounts', [publicAccount.key]);
                    changeAccount(targetAccount);
                }

                return;
            }

            const accounts = allAccounts
                ?.filter((account) => {
                    if (user.role === 'admin') {
                        if (
                            isViewAsActive === false ||
                            (isViewAsActive === true &&
                                (account.liveAccount === true ||
                                    account.broker === 'simulator' ||
                                    account.ptt_account ||
                                    account.pct_account ||
                                    account.simulator_account))
                        ) {
                            if (effectiveCompany === 'Bear Bull Traders') {
                                return account.ptt_account && account.ptt_account !== '';
                            }
                            if (effectiveCompany === 'Peak Capital Trading') {
                                return account.pct_account && account.pct_account !== '';
                            }
                            if (effectiveCompany === 'Trading Terminal') {
                                return (
                                    account.simulator_account && account.simulator_account !== ''
                                );
                            }
                            if (effectiveCompany === 'Personal') {
                                return (
                                    account.liveAccount === false && account.broker !== 'simulator'
                                );
                            }
                            return false;
                        }
                        return false;
                    }

                    return true;
                })
                .filter((account) => {
                    const accountCopy = { ...account };

                    if (user.role === 'admin') {
                        if (
                            effectiveCompany === 'Bear Bull Traders' &&
                            account.liveAccount === true &&
                            account.ptt_account &&
                            account.ptt_account !== ''
                        ) {
                            if (!account.unassigned_view) accountCopy.label = 'BBT Live Trade';
                            return accountCopy;
                        }
                        if (
                            effectiveCompany === 'Peak Capital Trading' &&
                            account.liveAccount === true &&
                            account.pct_account &&
                            account.pct_account !== ''
                        ) {
                            if (!account.unassigned_view) accountCopy.label = 'PCT Live Trade';
                            return accountCopy;
                        }
                        if (
                            effectiveCompany === 'Trading Terminal' &&
                            account.simulator_account &&
                            account.simulator_account !== ''
                        ) {
                            // accountCopy.label = 'Simulator Account';
                            return accountCopy;
                        }
                        if (effectiveCompany === 'Personal' && account.liveAccount === false) {
                            return accountCopy;
                        }
                    } else {
                        return account;
                    }

                    return null;
                });

            setUserAccounts(accounts);

            if (accounts.length === 0) {
                if (hasSelectedAccounts) changeFilter('accounts', []);
                return;
            }

            const selectedKey = Array.isArray(filter.accounts) ? filter.accounts[0] : null;
            const selectedExists = selectedKey
                ? accounts.some((acc) => acc.key === selectedKey)
                : false;
            if (!selectedExists) {
                changeFilter('accounts', [accounts[0].key]);
                changeAccount(accounts[0]);
            }
        }

        // eslint-disable-next-line padded-blocks -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
    }, [
        allAccounts,
        effectiveCompany,
        filter.accounts,
        hasSelectedAccounts,
        isViewAsActive,
        publicView.isActive,
        normalizedPublicId,
        user.role,
        changeFilter,
        changeAccount
    ]);
    const accountsOptions = userAccounts.map((i) => ({
        label: i.label,
        value: i.key,
        liveAccount: i.liveAccount
    }));

    const hasLiveAccounts = accountsOptions.some((acc) => acc.liveAccount);

    if (hasLiveAccounts) {
        accountsOptions.sort((a, b) => {
            if (!a.liveAccount) {
                return -1;
            }
            if (b.liveAccount) {
                return 1;
            }
            return 0;
        });
    }

    return accountsOptions.length > 0 ? (
        <Menu
            mode="horizontal"
            className="subheader-container"
            onSelect={onChangeAccounts}
            selectedKeys={filter.accounts}
            defaultSelectedKeys={[accountsOptions[0].value]}
        >
            {accountsOptions.map((acc) => (
                <Menu.Item key={acc.value}>{acc.label}</Menu.Item>
            ))}
        </Menu>
    ) : (
        <Menu mode="horizontal" className="subheader-container">
            <Menu.Item disabled />
        </Menu>
    );
};

export default SubHeader;
